<script>
import formatCurrency from '@utils/format-currency'
import { formatOrder } from '@utils/format-order'
import formatPrice from '@utils/format-price'
import formatPaymentMethod from '@utils/format-payment-method'
import { conditionList } from '@utils/is-condition'
import { orderReserveParamsList } from '@utils/ipetbooking-order'
import { hotelOrderItemAdjustmentRows } from '@utils/hotel-order-billing'

export default {
  name: 'HotelMemberOrderBilling',
  props: {
    containerItem: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      taxTypeMap: {
        B2C: '個人',
        B2B: '公司',
        DONATE: '捐贈發票',
      },
    }
  },
  computed: {
    priceTotal() {
      const ordersListTotal = (this.containerItem.orders && this.containerItem.orders.items
        ? this.containerItem.orders.items
        : []
      ).reduce((s, o) => s + (Number(o.total) || 0), 0)
      const paramsListTotal = (this.containerItem.params || []).reduce(
        (s, p) => s + (Number(p.total) || 0),
        0
      )
      return ordersListTotal + paramsListTotal
    },
    orderBillingParamsList() {
      const params = this.containerItem.params || []
      const total = (this.containerItem.orders && this.containerItem.orders.items
        ? this.containerItem.orders.items
        : []
      ).reduce((s, o) => s + (Number(o.total) || 0), 0)
      const adjustmentTypes = ['coupon', '+', '-', '%']
      const adjustmentRows = orderReserveParamsList({
        typeList: adjustmentTypes,
        params,
        isUnpaidAndDeduct: false,
      }).filter(Boolean)
      const otherRows = params
        .filter((p) => p && !adjustmentTypes.includes(p.type))
        .map((p) => {
          const amount = Number(p.total)
          const isNeg = amount < 0
          return {
            title: p.title || '項目',
            text: isNeg
              ? `- NT$ ${formatCurrency(Math.abs(amount))}`
              : `NT$ ${formatCurrency(amount)}`,
            class: isNeg ? 'text-danger' : '',
            textClass: 'font-number',
          }
        })
      return conditionList([
        {
          title: '小計',
          text: `NT$ ${formatCurrency(total)}`,
          textClass: 'font-number',
        },
        ...adjustmentRows,
        ...otherRows,
      ])
    },
    orderBillingTotal() {
      const orderStatus = formatOrder(this.containerItem)
      const paymentStatusText =
        this.containerItem.paymentStatus === 'PAID'
          ? '已付款'
          : this.containerItem.paymentStatus === 'UNPAID'
            ? '未付款'
            : ''
      return conditionList([
        {
          title: `<span class="text-${orderStatus.color} fw-semi-bold">付款金額</span><br><span class="text-gray-2 fs-2sm">${paymentStatusText}</span>`,
          text: `${formatPrice(this.priceTotal)}`,
          class: 'align-items-md-end align-items-center mt-2',
          textClass: `font-number fs-xl fw-semi-bold text-${orderStatus.infoColor}`,
        },
      ])
    },
    displayPaymentMethod() {
      return this.containerItem.paymentMethod
        ? formatPaymentMethod(this.containerItem.paymentMethod)
        : '尚未選擇付款方式'
    },
    hasInvoice() {
      return (
        this.containerItem.invoice &&
        this.containerItem.invoice.InvoiceNumber !== null &&
        this.containerItem.invoice.InvoiceNumber !== undefined
      )
    },
  },
  methods: {
    formatCurrency,
    formatPrice,
    carrierTypeMap(key) {
      const map = {
        0: '手機條碼',
        1: '自然人憑證條碼',
        2: 'ezPay 電子發票載具',
      }
      if (key == null) return '無（寄送電子發票至客戶 email）'
      return map[key]
    },
    formatBookingStayDateYmd(booking) {
      const raw = booking && (booking.date || booking.bookingDate)
      if (raw == null || raw === '') return ''
      if (typeof raw === 'string') return raw.split('T')[0]
      return String(raw)
    },
    bookingDetailTitle(booking) {
      const title = (booking && booking.title) || '房型'
      const ymd = this.formatBookingStayDateYmd(booking)
      return ymd ? `${title} · ${ymd}` : title
    },
    orderBillingList(item) {
      const bookings = item.bookings || []
      const bookingList = bookings.map((booking) => ({
        title: this.bookingDetailTitle(booking),
        text: `NT$ ${formatCurrency(booking.price)}`,
        textClass: 'font-number',
      }))
      const adjustmentRows = hotelOrderItemAdjustmentRows(item)
      return conditionList([
        ...bookingList,
        ...adjustmentRows,
        { type: 'hr' },
        {
          title: '小計',
          text: `NT$ ${formatCurrency(item.total || 0)}`,
          textClass: 'font-number',
        },
      ])
    },
  },
}
</script>

<template>
  <div :class="[$style.hotelMemberBilling, { [$style.isCompact]: compact }]">
    <h3 v-if="!compact" :class="$style.sectionTitle">
      訂單明細
    </h3>
    <template v-for="(item, $index) in (containerItem.orders && containerItem.orders.items) || []">
      <div :key="'room-' + $index" :class="$style.roomBlock">
        <div :class="$style.roomHead">
          <div class="d-flex align-items-center">
            <i
              v-if="!$globalIsDingBeauty"
              :class="['fontello', 'fontello-paw', 'mr-2', $style.accentText]"
            />
            <i
              v-else
              :class="['fontello', 'fontello-db-lipstick', 'mr-2', $style.accentText]"
            />
            <span
              v-if="item.guests && item.guests[0]"
              :class="[$style.accentText, 'mr-3']"
            >{{ item.guests[0].name }}</span>
            <span
              v-if="item.guests && item.guests[0] && item.guests[0].category"
              class="text-gray-2 fs-sm"
            >{{ item.guests[0].category.title }}</span>
          </div>
        </div>
        <BaseList
          :data-list="orderBillingList(item)"
          class="px-md-4 px-2"
          type="order"
        />
      </div>
    </template>
    <template v-if="containerItem.params && containerItem.params[0]">
      <hr class="my-2">
      <BaseList
        :data-list="orderBillingParamsList"
        class="px-md-4 px-2"
        type="order"
      />
    </template>
    <div>
      <div :class="$style.hr" />
      <BaseList
        :data-list="orderBillingTotal"
        class="px-md-4 px-2"
        type="order"
      />
      <div class="px-md-4 px-2 mt-3">
        <div class="d-flex align-items-center justify-content-between">
          <span class="text-gray-2">付款方式</span>
          <span class="fw-medium">{{ displayPaymentMethod }}</span>
        </div>
      </div>
    </div>
    <div v-if="hasInvoice" :class="$style.invoiceBox">
      <p :class="$style.invoiceTitle">
        發票資訊
      </p>
      <div class="invoice-detail__item">
        <p class="item-name">
          發票日期
        </p>
        <p class="item-value">
          {{ containerItem.invoice.CreateTime }}
        </p>
      </div>
      <div class="invoice-detail__item">
        <p class="item-name">
          發票號碼
        </p>
        <p class="item-value">
          {{ containerItem.invoice.InvoiceNumber }}
        </p>
      </div>
      <div class="invoice-detail__item">
        <p class="item-name">
          隨機碼
        </p>
        <p class="item-value">
          {{ containerItem.invoice.RandomNum }}
        </p>
      </div>
      <div class="invoice-detail__item">
        <p class="item-name">
          發票形式
        </p>
        <p class="item-value">
          {{ taxTypeMap[containerItem.invoice.taxType] }}
        </p>
      </div>
      <div
        v-if="containerItem.invoice.taxType === 'B2C'"
        class="invoice-detail__item"
      >
        <p class="item-name" style="margin-bottom: 0">
          載具類型
        </p>
        <p class="item-value" style="margin-bottom: 0">
          {{ carrierTypeMap(containerItem.invoice.CarrierType) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.hotelMemberBilling {
  padding: 1.5rem 1.25rem;
  border: 1px solid rgba($color-primary, 0.35);
  border-radius: 8px;
  background: #fff;

  &.isCompact {
    padding: 1rem;
    border: none;
  }

  .sectionTitle {
    font-size: 1.125rem;
    font-weight: 600;
    color: $color-primary-dark;
    margin-bottom: 1.25rem;
  }

  .roomHead {
    padding: 0.75rem 1rem;
    margin: 0 -0.25rem 1rem;
    font-size: 0.875rem;
    background: rgba($color-primary, 0.1);
    border-radius: 4px;
  }

  .accentText {
    color: $color-primary;
  }

  .roomBlock:not(:last-of-type) {
    margin-bottom: 1.5rem;
  }

  .hr {
    @include hr_decoration();
    margin: 0.5rem 0;
  }

  .invoiceBox {
    border: 1px solid rgba(104, 117, 128, 0.3);
    padding: 16px 20px;
    margin-top: 20px;
    border-radius: 4px;
  }

  .invoiceTitle {
    font-size: 1rem;
    color: $color-gray-2;
    margin-bottom: 0.5rem;
  }

  :global {
    .invoice-detail__item {
      display: flex;
      justify-content: space-between;
      p {
        margin-bottom: 0.5rem;
      }
    }
  }
}
</style>
